// onboardingApi.js - API utilities for onboarding operations

// IMPORTANT: You will need to import fetchAuthSession from aws-amplify/auth
// Ensure you have `aws-amplify` installed and configured in your project (e.g., in index.js)
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 * It's crucial that a user is logged in for this to return a valid token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    // The ID Token is typically what API Gateway expects for authorization.
    if (tokens && tokens.idToken) {
      // Check if token is expired
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for onboarding API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Individual User Onboarding API
 * Creates a new user with the provided information
 * Matches the Lambda function: rakshak-rakshak-individual-onboarding
 * Endpoint: /user/create (based on lambdas repository)
 */
export const createIndividualUser = async userData => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    // Use only the working /user/create endpoint
    console.log(`🔍 Calling endpoint: ${API_BASE_URL}/user/create`);
    console.log(`🔑 Using token: ${token.substring(0, 20)}...`);

    // Log the complete request details
    console.log('📤 ** REQUEST DETAILS ** 📤');
    console.log('🌐 URL:', `${API_BASE_URL}/user/create`);
    console.log('📋 Method: POST');
    console.log('🔑 Headers:', {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.substring(0, 20)}...`,
    });
    console.log('📦 Request Body:', JSON.stringify(userData, null, 2));
    console.log('📤 ** END REQUEST DETAILS ** 📤');

    const response = await fetch(`${API_BASE_URL}/user/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      // Log the response details for debugging
      console.log(`❌ HTTP Error: ${response.status} ${response.statusText}`);
      console.log(
        `🔍 Response Headers:`,
        Object.fromEntries(response.headers.entries())
      );

      // Try to get response text first to see what we're actually getting
      const responseText = await response.text();
      console.log(`📄 Response Body (raw):`, responseText);
      console.log(`📄 Response Body (length):`, responseText.length);

      // Try to parse as JSON if possible
      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        console.log(`📄 Parsed JSON error:`, errorData);
        errorMessage =
          errorData.message ||
          errorData.error ||
          errorData.errorMessage ||
          JSON.stringify(errorData) ||
          `HTTP error! status: ${response.status}`;
      } catch (parseError) {
        // If it's not JSON, use the text content directly (it's likely a plain string message)
        console.log(`📄 Not JSON, using text content`);
        if (response.status === 409 && responseText.trim()) {
          errorMessage = responseText.trim();
        } else if (response.status === 500 && responseText.trim()) {
          // For 500 errors, show the actual error message (often contains DynamoDB errors)
          errorMessage = responseText.trim();
        } else if (responseText.trim()) {
          // Use the text content if available
          errorMessage = responseText.trim();
        } else {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
      }

      console.log(`📄 Final error message:`, errorMessage);

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/user/create';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API error: ${response.status} ${response.statusText} for createIndividualUser`,
        error,
        {
          endpoint: '/user/create',
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating individual user:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API network/fetch error for createIndividualUser`,
        error,
        {
          endpoint: '/user/create',
          method: 'POST',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Driver User Onboarding API
 * Creates a new driver with the provided information
 * Uses a different endpoint: /driver/user/create
 * Matches the Lambda function: rakshak-individual-onboarding-driver
 */
export const createDriverUser = async driverData => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(
      `🔍 Calling driver endpoint: ${API_BASE_URL}/driver/user/create`
    );
    console.log(`🔑 Using token: ${token.substring(0, 20)}...`);

    // Log the complete request details
    console.log('📤 ** DRIVER REQUEST DETAILS ** 📤');
    console.log('🌐 URL:', `${API_BASE_URL}/driver/user/create`);
    console.log('📋 Method: POST');
    console.log('🔑 Headers:', {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.substring(0, 20)}...`,
    });
    console.log('📦 Request Body:', JSON.stringify(driverData, null, 2));
    console.log('📤 ** END DRIVER REQUEST DETAILS ** 📤');

    const response = await fetch(`${API_BASE_URL}/driver/user/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(driverData),
    });

    if (!response.ok) {
      // Log the response details for debugging
      console.log(`❌ HTTP Error: ${response.status} ${response.statusText}`);
      console.log(
        `🔍 Response Headers:`,
        Object.fromEntries(response.headers.entries())
      );

      // Try to get response text first to see what we're actually getting
      const responseText = await response.text();
      console.log(`📄 Response Body (raw):`, responseText);
      console.log(`📄 Response Body (length):`, responseText.length);

      // Try to parse as JSON if possible
      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        console.log(`📄 Parsed JSON error:`, errorData);
        errorMessage =
          errorData.message ||
          errorData.error ||
          errorData.errorMessage ||
          JSON.stringify(errorData) ||
          `HTTP error! status: ${response.status}`;
      } catch (parseError) {
        // If it's not JSON, use the text content directly
        console.log(`📄 Not JSON, using text content`);
        if (response.status === 409 && responseText.trim()) {
          errorMessage = responseText.trim();
        } else if (response.status === 500 && responseText.trim()) {
          // For 500 errors, show the actual error message
          errorMessage = responseText.trim();
        } else if (responseText.trim()) {
          // Use the text content if available
          errorMessage = responseText.trim();
        } else {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
      }

      console.log(`📄 Final error message:`, errorMessage);

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/driver/user/create';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API error: ${response.status} ${response.statusText} for createDriverUser`,
        error,
        {
          endpoint: '/driver/user/create',
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating driver user:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API network/fetch error for createDriverUser`,
        error,
        {
          endpoint: '/driver/user/create',
          method: 'POST',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Test CloudFront Access
 * Tests if the CloudFront issue affects other endpoints
 */
export const testCloudFrontAccess = async () => {
  try {
    console.log('🧪 Testing CloudFront access...');
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication token is missing');
    }

    // Test multiple endpoints to see which ones work
    const testEndpoints = ['/test'];

    for (const endpoint of testEndpoints) {
      try {
        console.log(`🔍 Testing endpoint: ${endpoint}`);

        // Log request details for comparison
        console.log(`📤 ** TEST REQUEST: ${endpoint} ** 📤`);
        console.log(`🌐 URL: ${API_BASE_URL}${endpoint}`);
        console.log(`📋 Method: GET`);
        console.log(`🔑 Headers:`, {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.substring(0, 20)}...`,
        });
        console.log(`📤 ** END TEST REQUEST ** 📤`);

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          console.log(`✅ ${endpoint} - SUCCESS (${response.status})`);
          try {
            const data = await response.json();
            console.log(`📊 Response data:`, data);
          } catch (e) {
            console.log(`📄 Response is not JSON`);
          }
        } else {
          console.log(
            `❌ ${endpoint} - FAILED (${response.status} ${response.statusText})`
          );

          // Check if it's also a CloudFront error
          const responseText = await response.text();
          if (
            responseText.includes('CloudFront') ||
            responseText.includes('cloudfront')
          ) {
            console.log(`🌐 ${endpoint} - CloudFront blocked`);
          } else {
            console.log(
              `📄 ${endpoint} - Backend error: ${responseText.substring(
                0,
                100
              )}...`
            );
          }
        }
      } catch (error) {
        console.log(`❌ ${endpoint} - Network error: ${error.message}`);
      }
    }

    return true;
  } catch (error) {
    console.error('❌ CloudFront test failed:', error);
    return false;
  }
};

/**
 * Bulk User Onboarding API
 * Creates multiple users at once
 */
export const createBulkUsers = async usersData => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ users: usersData }),
    });

    if (!response.ok) {
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { message: responseText || `HTTP error! status: ${response.status}` };
      }

      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/api/onboarding/bulk';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_bulk_onboarding',
        `Bulk Onboarding API error: ${response.status} ${response.statusText} for createBulkUsers`,
        error,
        {
          endpoint: '/api/onboarding/bulk',
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating bulk users:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_bulk_onboarding',
        `Bulk Onboarding API network/fetch error for createBulkUsers`,
        error,
        {
          endpoint: '/api/onboarding/bulk',
          method: 'POST',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Vehicle Onboarding API
 * Creates a new vehicle record
 */
export const createVehicle = async vehicleData => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/vehicle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(vehicleData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }
};

/**
 * Site Onboarding API
 * Creates a new site record
 */
export const createSite = async siteData => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/site`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(siteData),
    });

    if (!response.ok) {
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { message: responseText || `HTTP error! status: ${response.status}` };
      }

      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/api/onboarding/site';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for createSite`,
        error,
        {
          endpoint: '/api/onboarding/site',
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating site:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for createSite`,
        error,
        {
          endpoint: '/api/onboarding/site',
          method: 'POST',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get All Roles API
 * Fetches available roles from the system based on user's vertical/tenant
 * Uses the endpoint: /user/roles/by-vertical - GET method
 * Now uses /tenant API to get correct tenant data from database
 */
export const getAllRoles = async () => {
  try {
    console.log('🔍 Getting tenant info from /tenant API...');
    console.log('🔍 Full tenant API URL:', `${API_BASE_URL}/tenant`);

    // Get authentication token first
    const token = await getAuthToken();
    if (!token) {
      console.warn('No authentication token available, using fallback roles');
      return getFallbackRoles();
    }

    // Get tenant info from database via /tenant API
    const tenantResponse = await fetch(`${API_BASE_URL}/tenant`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    console.log('🔍 /tenant API response status:', tenantResponse.status);
    console.log(
      '🔍 /tenant API response headers:',
      tenantResponse.headers.get('content-type')
    );

    if (!tenantResponse.ok) {
      console.error(
        '❌ Failed to fetch tenant info from /tenant API:',
        tenantResponse.status
      );
      const errorText = await tenantResponse.text();
      console.error('❌ Error response body:', errorText);
      
      // Log API error to logging service
      const error = new Error(`Failed to fetch tenant info: ${tenantResponse.status}`);
      error.status = tenantResponse.status;
      error.statusText = tenantResponse.statusText;
      error.endpoint = '/tenant';
      error.responseData = errorText;
      
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API error: ${tenantResponse.status} ${tenantResponse.statusText} for getAllRoles (tenant API)`,
        error,
        {
          endpoint: '/tenant',
          method: 'GET',
          status: tenantResponse.status,
          statusText: tenantResponse.statusText,
          responseData: errorText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
      
      console.warn('Using fallback roles due to tenant API failure');
      return getFallbackRoles();
    }

    // Check if response is JSON
    const contentType = tenantResponse.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await tenantResponse.text();
      console.error('❌ /tenant API returned non-JSON response:', responseText);
      console.warn('Using fallback roles due to non-JSON response');
      return getFallbackRoles();
    }

    const tenantData = await tenantResponse.json();
    console.log('✅ Tenant data from /tenant API:', tenantData);

    const businessVertical = tenantData.data?.business_vertical;

    if (!businessVertical) {
      console.warn(
        '❌ No business_vertical found in tenant data, using fallback roles'
      );
      return getFallbackRoles();
    }

    console.log('✅ Business vertical from database:', businessVertical);

    // Try the business vertical from database first, then fallback options
    const businessVerticalToTry = [
      businessVertical, // Use the business vertical from /tenant API
      'facility_management',
      'security',
      'healthcare',
      'retail',
      'hospitality',
    ];

    let rolesData = null;
    let successfulVertical = null;

    // Try each business vertical until we find one that returns roles
    for (const vertical of businessVerticalToTry) {
      if (!vertical) continue;

      const endpoint = `${API_BASE_URL}/user/roles/by-vertical?business_vertical=${vertical}`;
      console.log(
        `🔍 Trying roles endpoint with vertical "${vertical}":`,
        endpoint
      );

      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Success with vertical "${vertical}":`, data);

          // Check if we got roles back
          if (
            data.roles &&
            Array.isArray(data.roles) &&
            data.roles.length > 0
          ) {
            rolesData = data.roles;
            successfulVertical = vertical;
            console.log(
              `🎉 Found ${data.roles.length} roles for vertical "${vertical}"`
            );
            break;
          } else {
            console.log(`⚠️ Vertical "${vertical}" returned empty roles array`);
          }
        } else {
          console.log(
            `❌ Vertical "${vertical}" failed with status:`,
            response.status
          );
          if (response.status === 400) {
            try {
              const errorDetails = await response.json();
              console.log(`🔍 400 Error for "${vertical}":`, errorDetails);
            } catch (e) {
              console.log(`🔍 Could not parse error for "${vertical}"`);
            }
          }
        }
      } catch (error) {
        console.log(
          `❌ Network error for vertical "${vertical}":`,
          error.message
        );
      }
    }

    if (rolesData && successfulVertical) {
      console.log(
        `🎯 Using vertical "${successfulVertical}" with ${rolesData.length} roles`
      );
      return rolesData;
    }

    console.warn('❌ No business vertical returned roles, using fallback');
    return getFallbackRoles();
  } catch (error) {
    console.error('Error fetching roles:', error);
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_user_onboarding',
      `User Onboarding API network/fetch error for getAllRoles`,
      error,
      {
        endpoint: '/user/roles/by-vertical',
        method: 'GET',
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[onboardingApi] Failed to log error:', logError);
    });
    
    console.log('Using fallback roles due to error');
    return getFallbackRoles();
  }
};

/**
 * Test API Endpoint
 * Tests if the API is accessible and returns user authentication data
 */
export const testApiConnection = async () => {
  try {
    console.log('🧪 Testing API connection...');
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    const response = await fetch(`${API_BASE_URL}/test`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const userData = await response.json();
    console.log('✅ API test successful, user data:', userData);
    return userData;
  } catch (error) {
    console.error('❌ API test failed:', error);
    throw error;
  }
};

/**
 * Get User Tenant Info
 * Fetches the current user's tenant information from the /test endpoint
 * This provides real user context including tenant_id and employee_id
 */
export const getUserTenantInfo = async () => {
  try {
    // Get real user data from the /test endpoint
    const userData = await testApiConnection();

    // Extract tenant information from the JWT claims
    // Try to get business_vertical from user data
    let businessVertical =
      userData['custom:business_vertical'] ||
      userData['business_vertical'] ||
      userData['vertical'];

    // If not found in user data, try to determine from tenant_id or other factors
    if (!businessVertical) {
      const tenantId = userData['custom:tenant_id'] || '0';

      // Try different verticals based on tenant or default to a list to test
      // You can customize this logic based on your tenant setup
      if (tenantId === '0') {
        // For tenant 0, try these common verticals
        businessVertical = 'facility_management'; // Try this first as it's more common
      } else {
        businessVertical = 'security'; // Fallback for other tenants
      }
    }

    console.log('🔍 Extracted business_vertical:', businessVertical);
    console.log(
      '🔍 Full user data for debugging:',
      JSON.stringify(userData, null, 2)
    );

    return {
      tenant_id: userData['custom:tenant_id'] || '0',
      employee_id: userData['custom:employee_id'] || 'EMP0000',
      business_vertical: businessVertical,
      tenant_name: `Tenant ${userData['custom:tenant_id'] || '0'}`,
      user_name: userData.name || 'Unknown User',
      // user_email: userData.email || 'unknown@example.com',
      user_role: userData['cognito:groups'] || 'Unknown Role',
      user_id: userData.sub || 'unknown',
    };
  } catch (error) {
    console.warn(
      'Failed to get user tenant info from API, using fallback:',
      error
    );
    // Fallback to placeholder data if API fails
    return {
      tenant_id: '0', // Default tenant ID
      business_vertical: 'security',
      tenant_name: 'Default Tenant',
      employee_id: 'EMP0000',
      user_name: 'Unknown User',
      // user_email: 'unknown@example.com',
      user_role: 'Unknown Role',
      user_id: 'unknown',
    };
  }
};

/**
 * Fallback roles when API is not available
 * These should match the roles available in your system
 */
const getFallbackRoles = () => {
  return [
    { role_name: 'driver', display_name: 'Driver' },
    { role_name: 'security_officer', display_name: 'Security Officer' },
    { role_name: 'supervisor', display_name: 'Supervisor' },
    { role_name: 'manager', display_name: 'Manager' },
    { role_name: 'admin', display_name: 'Admin' },
  ];
};

/**
 * Get Users By Role API
 * Fetches users with a specific role (for managers/supervisors)
 * Matches the Flutter API endpoints: /user/supervisors, /user/guards
 * Note: These endpoints require authentication tokens
 */
export const getUsersByRole = async role => {
  try {
    let endpoint;

    // Use the exact endpoints from Flutter code
    if (role === 'supervisor') {
      endpoint = `${API_BASE_URL}/user/supervisors`;
    } else if (role === 'manager') {
      endpoint = `${API_BASE_URL}/user/guards`; // Guards are typically managers
    } else {
      // Fallback to the general endpoint for other roles
      endpoint = `${API_BASE_URL}/user/getall/by-spec-role?role=${role}`;
    }

    console.log(`🔍 Calling endpoint: ${endpoint}`);

    const token = await getAuthToken();
    if (!token) {
      console.warn('No authentication token available, returning empty array');
      return [];
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('Authentication required for this endpoint');
        return []; // Return empty array for auth errors
      }
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { message: responseText || `HTTP error! status: ${response.status}` };
      }

      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API error: ${response.status} ${response.statusText} for getUsersByRole`,
        error,
        {
          endpoint,
          method: 'GET',
          role,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    const users = await response.json();
    console.log(`✅ Users fetched for role ${role}:`, users);
    return users;
  } catch (error) {
    console.error(`❌ Error fetching users by role ${role}:`, error);

    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API network/fetch error for getUsersByRole`,
        error,
        {
          endpoint: endpoint || 'unknown',
          method: 'GET',
          role,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }

    // If it's a CORS or network error, return empty array instead of throwing
    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('CORS')
    ) {
      console.warn('Network/CORS error, returning empty array');
      return [];
    }

    throw error;
  }
};

/**
 * Get All Sites API
 * Fetches available sites for role-based field requirements
 * Matches the Lambda function: rakshak-getall-sites
 */
export const getAllSites = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    const response = await fetch(`${API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { message: responseText || `HTTP error! status: ${response.status}` };
      }

      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/getall';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API error: ${response.status} ${response.statusText} for getAllSites`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });

      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching sites:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_user_onboarding',
        `User Onboarding API network/fetch error for getAllSites`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[onboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Onboarding Status API
 * Retrieves the status of onboarding processes
 */
export const getOnboardingStatus = async onboardingId => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/onboarding/status/${onboardingId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    throw error;
  }
};

/**
 * Update Onboarding Record API
 * Updates an existing onboarding record
 */
export const updateOnboardingRecord = async (onboardingId, updateData) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/onboarding/${onboardingId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating onboarding record:', error);
    throw error;
  }
};

/**
 * Delete Onboarding Record API
 * Deletes an onboarding record
 */
export const deleteOnboardingRecord = async onboardingId => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/onboarding/${onboardingId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting onboarding record:', error);
    throw error;
  }
};

/**
 * Get All Onboarding Records API
 * Retrieves all onboarding records with optional filtering
 */
export const getAllOnboardingRecords = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters);
    const response = await fetch(
      `${API_BASE_URL}/api/onboarding?${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting onboarding records:', error);
    throw error;
  }
};

/**
 * Upload File API
 * Handles file uploads for onboarding (images, documents, etc.)
 */
export const uploadFile = async (file, type = 'profile') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetch(`${API_BASE_URL}/api/onboarding/upload`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, let the browser set it
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Validate Onboarding Data API
 * Validates onboarding data before submission
 */
export const validateOnboardingData = async (data, type = 'individual') => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ data, type }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error validating onboarding data:', error);
    throw error;
  }
};

/**
 * Enhanced Image Compression Utility for API Gateway
 * Reduces payload size while maintaining image quality
 * Multiple compression levels with fallback mechanisms
 */
export const compressImage = (
  base64String,
  maxWidth = 800,
  maxHeight = 600,
  quality = 0.7,
  targetSizeKB = 300
) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate optimal dimensions
      let { width, height } = img;
      const originalWidth = width;
      const originalHeight = height;

      // Smart resizing based on original size
      if (width > height) {
        // Landscape image
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        // Portrait or square image
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw with high quality settings
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Multi-level compression with fallback
      const compressionLevels = [
        { quality: 0.8, maxSize: 400 }, // High quality
        { quality: 0.6, maxSize: 300 }, // Medium quality
        { quality: 0.4, maxSize: 250 }, // Low quality
        { quality: 0.2, maxSize: 200 }, // Very low quality
      ];

      let bestResult = null;
      let smallestSize = Infinity;

      // Try different compression levels
      for (const level of compressionLevels) {
        const compressedBase64 = canvas.toDataURL('image/jpeg', level.quality);
        const compressedData = compressedBase64.split(',')[1];
        const sizeKB = compressedData.length / 1024;

        console.log(
          `📸 Compression attempt (q=${level.quality}): ${sizeKB.toFixed(1)} KB`
        );

        if (sizeKB <= level.maxSize && sizeKB < smallestSize) {
          bestResult = compressedData;
          smallestSize = sizeKB;
        }

        // If we achieve target size, stop trying higher qualities
        if (sizeKB <= targetSizeKB) {
          break;
        }
      }

      if (!bestResult) {
        // If still too large, try extreme compression
        console.warn('⚠️ Attempting extreme compression...');
        const extremeCompressed = canvas.toDataURL('image/jpeg', 0.1);
        const extremeData = extremeCompressed.split(',')[1];
        const extremeSizeKB = extremeData.length / 1024;

        console.log(`📸 Extreme compression: ${extremeSizeKB.toFixed(1)} KB`);

        if (extremeSizeKB <= 500) {
          // 500KB absolute limit
          bestResult = extremeData;
        }
      }

      if (!bestResult) {
        reject(
          new Error(
            `Image compression failed. Original: ${(
              base64String.length / 1024
            ).toFixed(1)} KB. ` + `Please use a smaller image (under 1MB).`
          )
        );
        return;
      }

      const finalSizeKB = bestResult.length / 1024;
      const compressionRatio = (
        ((base64String.length - bestResult.length) / base64String.length) *
        100
      ).toFixed(1);

      console.log(
        `✅ Final compression: ${(base64String.length / 1024).toFixed(
          1
        )} KB → ${finalSizeKB.toFixed(1)} KB (${compressionRatio}% reduction)`
      );
      console.log(
        `📐 Dimensions: ${originalWidth}x${originalHeight} → ${Math.round(
          width
        )}x${Math.round(height)}`
      );

      resolve(bestResult);
    };

    img.onerror = () =>
      reject(new Error('Failed to load image for compression'));
    img.src = `data:image/jpeg;base64,${base64String}`;
  });
};

/**
 * Progressive Image Compression with Multiple Strategies
 */
export const progressiveCompressImage = async (base64String, options = {}) => {
  const {
    maxWidth = 800,
    maxHeight = 600,
    targetSizeKB = 300,
    maxAttempts = 3,
    onProgress = null,
  } = options;

  let currentBase64 = base64String;
  let bestResult = null;
  let bestSize = Infinity;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (onProgress) {
      onProgress(`Attempt ${attempt}/${maxAttempts}`, attempt / maxAttempts);
    }

    try {
      // Try different strategies for each attempt
      const strategies = [
        // Strategy 1: Standard compression
        () =>
          compressImage(currentBase64, maxWidth, maxHeight, 0.7, targetSizeKB),

        // Strategy 2: Aggressive resizing
        () =>
          compressImage(
            currentBase64,
            maxWidth * 0.8,
            maxHeight * 0.8,
            0.6,
            targetSizeKB
          ),

        // Strategy 3: Extreme compression
        () =>
          compressImage(
            currentBase64,
            maxWidth * 0.6,
            maxHeight * 0.6,
            0.4,
            targetSizeKB
          ),
      ];

      const strategy = strategies[Math.min(attempt - 1, strategies.length - 1)];
      const result = await strategy();

      const resultSizeKB = result.length / 1024;

      if (resultSizeKB < bestSize) {
        bestResult = result;
        bestSize = resultSizeKB;
      }

      // If we're under target size, we're done
      if (resultSizeKB <= targetSizeKB) {
        break;
      }

      // Prepare for next attempt
      currentBase64 = result;
    } catch (error) {
      console.warn(`⚠️ Compression attempt ${attempt} failed:`, error.message);
      continue;
    }
  }

  if (!bestResult) {
    throw new Error(
      `Progressive compression failed after ${maxAttempts} attempts. ` +
        `Original size: ${(base64String.length / 1024).toFixed(1)} KB. ` +
        `Please use a smaller image.`
    );
  }

  const finalSizeKB = bestResult.length / 1024;
  const totalReduction = (
    ((base64String.length - bestResult.length) / base64String.length) *
    100
  ).toFixed(1);

  console.log(
    `🎯 Progressive compression complete: ${(
      base64String.length / 1024
    ).toFixed(1)} KB → ${finalSizeKB.toFixed(
      1
    )} KB (${totalReduction}% reduction)`
  );

  return bestResult;
};

/**
 * Smart Image Preprocessing for Optimal Compression
 */
export const preprocessImageForCompression = base64String => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Analyze image characteristics
      const { width, height } = img;
      const aspectRatio = width / height;
      const originalSizeKB = base64String.length / 1024;

      // Determine optimal compression strategy
      let targetWidth, targetHeight, targetQuality, targetSizeKB;

      if (originalSizeKB > 2000) {
        // Very large images
        targetWidth = Math.min(width, 600);
        targetHeight = Math.min(height, 450);
        targetQuality = 0.3;
        targetSizeKB = 200;
      } else if (originalSizeKB > 1000) {
        // Large images
        targetWidth = Math.min(width, 800);
        targetHeight = Math.min(height, 600);
        targetQuality = 0.5;
        targetSizeKB = 300;
      } else if (originalSizeKB > 500) {
        // Medium images
        targetWidth = Math.min(width, 1000);
        targetHeight = Math.min(height, 750);
        targetQuality = 0.7;
        targetSizeKB = 400;
      } else {
        // Small images
        targetWidth = Math.min(width, 1200);
        targetHeight = Math.min(height, 900);
        targetQuality = 0.8;
        targetSizeKB = 500;
      }

      // Maintain aspect ratio
      if (aspectRatio > 1) {
        // Landscape
        targetHeight = targetWidth / aspectRatio;
      } else {
        // Portrait
        targetWidth = targetHeight * aspectRatio;
      }

      resolve({
        originalSizeKB,
        targetWidth: Math.round(targetWidth),
        targetHeight: Math.round(targetHeight),
        targetQuality,
        targetSizeKB,
        aspectRatio,
      });
    };

    img.onerror = () => reject(new Error('Failed to analyze image'));
    img.src = `data:image/jpeg;base64,${base64String}`;
  });
};

/**
 * Comprehensive Image Quality Assessment
 */
export const assessImageQuality = base64String => {
  return new Promise(resolve => {
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;
      const fileSizeKB = base64String.length / 1024;
      const pixels = width * height;
      const bitsPerPixel = (fileSizeKB * 8 * 1024) / pixels;

      // Quality assessment
      let quality = 'unknown';
      if (bitsPerPixel < 1) quality = 'very_low';
      else if (bitsPerPixel < 2) quality = 'low';
      else if (bitsPerPixel < 4) quality = 'medium';
      else if (bitsPerPixel < 8) quality = 'high';
      else quality = 'very_high';

      // Compression potential
      let compressionNeeded = 'none';
      if (fileSizeKB > 1000) compressionNeeded = 'heavy';
      else if (fileSizeKB > 500) compressionNeeded = 'moderate';
      else if (fileSizeKB > 200) compressionNeeded = 'light';
      else compressionNeeded = 'minimal';

      resolve({
        width,
        height,
        fileSizeKB,
        pixels,
        bitsPerPixel,
        quality,
        compressionNeeded,
        aspectRatio: width / height,
        isLandscape: width > height,
        isPortrait: height > width,
        isSquare: Math.abs(width - height) < 50,
      });
    };

    img.src = `data:image/jpeg;base64,${base64String}`;
  });
};

/**
 * Check payload size and provide recommendations
 */
export const checkPayloadSize = payload => {
  const payloadString = JSON.stringify(payload);
  const sizeInBytes = new Blob([payloadString]).size;
  const sizeInMB = sizeInBytes / (1024 * 1024);

  console.log(
    `📊 Payload size: ${sizeInMB.toFixed(2)} MB (${sizeInBytes} bytes)`
  );

  if (sizeInMB > 5) {
    console.warn(
      `⚠️ Payload size (${sizeInMB.toFixed(2)} MB) exceeds 5MB limit`
    );
    console.warn(
      '💡 Tip: Large payloads may cause CloudFront 403 errors. Consider removing the image.'
    );
    return {
      isTooLarge: true,
      size: sizeInMB,
      recommendation: 'Consider removing image or using a smaller image',
    };
  }

  return { isTooLarge: false, size: sizeInMB };
};

/**
 * Bulk onboarding functions
 */

/**
 * Process CSV data and validate user information
 */
export const processBulkOnboarding = async (csvData, roles) => {
  try {
    const lines = csvData.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

    const users = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const user = {};
        headers.forEach((header, index) => {
          user[header] = values[index] || '';
        });
        users.push(user);
      }
    }

    return users;
  } catch (error) {
    throw new Error(`Failed to process CSV data: ${error.message}`);
  }
};

/**
 * Validate individual user data for bulk onboarding
 */
export const validateUserData = (userData, roles) => {
  const errors = [];
  const requiredFields = [
    'Employee ID',
    'Full Name',
    'Date of Birth',
    'Phone Number',
    'Gender',
    'Blood Group',
    'Address',
    'Reports To',
    'Area Available',
    'Role Name',
  ];

  // Check required fields
  requiredFields.forEach(field => {
    if (!userData[field] || userData[field].trim() === '') {
      errors.push(`${field} is required`);
    }
  });

  // Validate date format (accept both YYYY-MM-DD and DD-MM-YYYY)
  if (userData['Date of Birth']) {
    const yyyyMMddRegex = /^\d{4}-\d{2}-\d{2}$/;
    const ddMMyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;

    if (
      !yyyyMMddRegex.test(userData['Date of Birth']) &&
      !ddMMyyyyRegex.test(userData['Date of Birth'])
    ) {
      errors.push(
        'Date of Birth must be in YYYY-MM-DD or DD-MM-YYYY format (e.g., 1990-01-01 or 01-01-1990)'
      );
    }
  }

  // Validate phone number
  if (
    userData['Phone Number'] &&
    !/^\+?[\d\s\-\(\)]+$/.test(userData['Phone Number'])
  ) {
    errors.push('Phone Number format is invalid');
  }

  // Validate role exists
  if (
    userData['Role Name'] &&
    !roles.find(role => role.role_name === userData['Role Name'])
  ) {
    errors.push(`Role "${userData['Role Name']}" not found`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Format date from YYYY-MM-DD to DD/MM/YYYY for lambda compatibility
 */
export const formatDateForLambda = dateString => {
  if (!dateString) return '';

  if (dateString.includes('-') && dateString.split('-').length === 3) {
    const parts = dateString.split('-');

    // Check if it's YYYY-MM-DD format
    if (parts[0].length === 4) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    // Check if it's DD-MM-YYYY format
    else if (parts[2].length === 4) {
      const [day, month, year] = parts;
      return `${day}/${month}/${year}`;
    }
  }

  return dateString;
};

/**
 * Download CSV template for bulk onboarding
 */
export const downloadTemplate = () => {
  const headers = [
    'Employee ID',
    'Full Name',
    'Date of Birth',
    'Phone Number',
    'Gender',
    'Blood Group',
    'Address',
    'Reports To',
    'Area Available',
    'Role Name',
    'Commercial License',
    'International Driving Permit',
    '4 Wheeler',
    'Heavy Motor',
  ];

  const sampleData = [
    'EMP001',
    'John Doe',
    '1990-01-01',
    '+911234567890',
    'Male',
    'O+',
    '123 Main Street City',
    'EMP0000',
    'North Zone',
    'Driver',
    'Yes',
    'No',
    'Yes',
    'No',
  ];

  const csvContent = [headers.join(','), sampleData.join(',')].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulk_onboarding_template.csv';
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Enhanced error handling for CloudFront issues
 */
export const handleCloudFrontError = (error, hasImage) => {
  if (error.message.includes('403') || error.message.includes('Forbidden')) {
    if (hasImage) {
      return {
        type: 'CLOUDFRONT_403_WITH_IMAGE',
        message:
          'CloudFront blocked the request (403 Forbidden). This often happens with large images.',
        solutions: [
          'Remove the image and try again',
          'Use a smaller image file (under 1MB)',
          'Check your internet connection',
          'Try again later (CloudFront may be experiencing issues)',
        ],
        recommendation:
          'Try removing the image first, as this resolves most CloudFront 403 errors.',
      };
    } else {
      return {
        type: 'CLOUDFRONT_403_NO_IMAGE',
        message:
          'CloudFront blocked the request (403 Forbidden) even without image.',
        solutions: [
          'Check your internet connection',
          'Verify the API endpoint is correct',
          'Try again later (CloudFront may be experiencing issues)',
          'Contact support if the issue persists',
        ],
        recommendation:
          'This may be a temporary CloudFront issue. Try again in a few minutes.',
      };
    }
  }

  return {
    type: 'OTHER_ERROR',
    message: error.message,
    solutions: [
      'Check the error details above',
      'Verify your input data',
      'Try again',
    ],
    recommendation: 'Review the error message and try again.',
  };
};

export default {
  createIndividualUser,
  createBulkUsers,
  createVehicle,
  createSite,
  getOnboardingStatus,
  updateOnboardingRecord,
  deleteOnboardingRecord,
  getAllOnboardingRecords,
  uploadFile,
  validateOnboardingData,
  testCloudFrontAccess,
  // Bulk onboarding functions
  processBulkOnboarding,
  validateUserData,
  formatDateForLambda,
  downloadTemplate,
};
